import React, { useEffect, useRef, useState } from 'react';
import { services } from '../mock';
import { Camera, Film, Users, Globe } from 'lucide-react';

const iconMap = {
  Camera,
  Film,
  Users,
  Globe
};

const Services = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards(prev => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 bg-[#fafafa] relative overflow-hidden">
      {/* 3D Background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-64 h-64 border border-black/5 transform -rotate-12 animate-spin-slow" />
        <div className="absolute bottom-40 right-1/3 w-48 h-48 border-2 border-black/5 transform rotate-45" style={{ animation: 'float-slow 8s ease-in-out infinite' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="mb-16 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-black" style={{
            textShadow: '2px 2px 0px rgba(0,0,0,0.03)'
          }}>
            Our Services
          </h2>
          <p className="text-base md:text-lg text-[#666666] max-w-2xl mx-auto font-light">
            AI-powered creative services that elevate your brand
          </p>
        </div>

        {/* Services grid - 4 items in 2x2 on desktop, 1 column on mobile */}
        <div 
          className="grid sm:grid-cols-2 gap-6 lg:gap-8"
          style={{
            perspective: '2000px',
            perspectiveOrigin: 'center center'
          }}
        >
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const isVisible = visibleCards.includes(index);
            
            return (
              <div
                key={service.id}
                ref={el => cardsRef.current[index] = el}
                className="group relative bg-white border border-black/10 p-8 lg:p-10 cursor-pointer"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: isVisible 
                    ? 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)' 
                    : 'perspective(1000px) rotateX(10deg) rotateY(-10deg) translateZ(-50px)',
                  opacity: isVisible ? 1 : 0,
                  transition: `all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.15}s`,
                  boxShadow: '15px 15px 40px rgba(0,0,0,0.08), -5px -5px 20px rgba(255,255,255,0.5)'
                }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const centerX = rect.width / 2;
                  const centerY = rect.height / 2;
                  const rotateX = (y - centerY) / 15;
                  const rotateY = (centerX - x) / 15;
                  e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(30px)`;
                  e.currentTarget.style.boxShadow = '25px 25px 60px rgba(0,0,0,0.15), -10px -10px 30px rgba(255,255,255,0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
                  e.currentTarget.style.boxShadow = '15px 15px 40px rgba(0,0,0,0.08), -5px -5px 20px rgba(255,255,255,0.5)';
                }}
              >
                {/* 3D depth indicator */}
                <div 
                  className="absolute inset-0 bg-black/5 -z-10"
                  style={{
                    transform: 'translateZ(-20px)',
                    filter: 'blur(10px)'
                  }}
                />

                {/* Icon with 3D effect */}
                <div className="mb-6 relative" style={{ transformStyle: 'preserve-3d' }}>
                  <div 
                    className="w-14 h-14 bg-black flex items-center justify-center group-hover:scale-110 transition-all duration-500 relative"
                    style={{
                      transform: 'translateZ(20px)',
                      boxShadow: '5px 5px 15px rgba(0,0,0,0.2)'
                    }}
                  >
                    {Icon && <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />}
                  </div>
                  {/* Icon shadow */}
                  <div 
                    className="absolute top-2 left-2 w-14 h-14 bg-black/20 -z-10"
                    style={{
                      transform: 'translateZ(-10px)',
                      filter: 'blur(5px)'
                    }}
                  />
                </div>

                {/* Content */}
                <h3 
                  className="text-2xl lg:text-3xl font-bold text-black mb-4"
                  style={{
                    transform: 'translateZ(10px)',
                    transformStyle: 'preserve-3d'
                  }}
                >
                  {service.title}
                </h3>
                <p 
                  className="text-[#666666] leading-relaxed font-light mb-6"
                  style={{
                    transform: 'translateZ(5px)',
                    transformStyle: 'preserve-3d'
                  }}
                >
                  {service.shortDescription}
                </p>
                
                {/* Pricing */}
                <div 
                  className="pt-4 border-t border-black/10"
                  style={{
                    transform: 'translateZ(5px)',
                    transformStyle: 'preserve-3d'
                  }}
                >
                  <p className="text-sm font-medium text-black">{service.pricing}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(45deg); }
          50% { transform: translateY(-40px) rotate(45deg); }
        }
      `}</style>
    </section>
  );
};

export default Services;